import React, { useCallback, useEffect, useRef, useState } from "react";
import Joyride, { Step } from "react-joyride";
import { useSearch } from "../context/SearchContext";
import ResponsePage from "./ResponsePage";
import SearchPage from "./SearchPage";
import { createRoot } from "react-dom/client";
import {
  sendFinishOnboardingClick,
  sendCloseOnboardingClick,
} from "../utils/analyticsEvents";

interface TutorialState {
  shown: boolean;
  timestamp: string;
}

const TUTORIAL_STORAGE_KEY = "tutorialState";

const getTutorialState = (): TutorialState => {
  const stored = localStorage.getItem(TUTORIAL_STORAGE_KEY);
  if (stored) {
    return JSON.parse(stored);
  }
  return { shown: false, timestamp: "" };
};

const setTutorialState = (shown: boolean) => {
  const state: TutorialState = {
    shown,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(state));
};

interface State {
  run: boolean;
  steps: Step[];
}

type TimeoutRef = ReturnType<typeof setTimeout> | null;

const MainPage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { hasSearched, isLoading, setWasSeen } = useSearch();
  const [tutorialStep, setTutorialStep] = useState(0);
  const spotlightTimeoutRef = useRef<TimeoutRef>(null);
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  const isActiveRef = useRef(false);
  const scrollDebounceRef = useRef<TimeoutRef>(null);
  const tutorialContent = useCallback((index: number) => {
    switch (index) {
      case 0:
        return (
          <div>
            <p className="font-flexo-demi text-left">
              Conoce tus productos preaprobados
            </p>
            <p className="text-sm mt-2 text-left">
              ¡Es rápido y solo te tomará unos segundos!
            </p>
          </div>
        );
      case 1:
        return (
          <div>
            <p className="font-flexo-demi text-left">
              Preguntas sugeridas para ti
            </p>
            <p className="text-sm mt-2 text-left">
              ¡Lleva tu búsqueda al siguiente nivel!
            </p>
          </div>
        );
      case 2:
        return (
          <div>
            <p className="font-flexo-demi text-left">
              Todo listo para iniciar tu búsqueda
            </p>
            <p className="text-sm mt-2 text-left">
              ¡Resuelve tus dudas al instante!
            </p>
          </div>
        );
    }
  }, []);

  const getSpotlightStyle = (selector: string) => {
    const element = document.querySelector(selector);
    if (!element) return {};

    const rect = element.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
  };

  const addExtraSpotlightStyle = () => {
    if (spotlightTimeoutRef.current) {
      clearTimeout(spotlightTimeoutRef.current);
    }

    const applySpotlight = () => {
      const overlay = document.querySelector(".react-joyride__overlay");
      const spotlight = document.querySelector(".react-joyride__spotlight");

      if (!overlay || !spotlight) {
        spotlightTimeoutRef.current = setTimeout(applySpotlight, 50);
        return;
      }

      removeExtraSpotlightStyles();

      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      const buttonBill = getSpotlightStyle(
        "#button-bill",
      ) as React.CSSProperties;

      const secondButton = getSpotlightStyle(
        windowWidth > 800 ? "#button-credit-card" : "#button-piggy-bank",
      ) as React.CSSProperties;

      const highlights = [buttonBill, secondButton];

      if (windowWidth >= 768 || windowHeight >= 800) {
        const additionalButton = getSpotlightStyle(
          windowWidth > 800 ? "#button-piggy-bank" : "#button-credit-card",
        ) as React.CSSProperties;

        const buttonCompass = getSpotlightStyle(
          "#button-compass",
        ) as React.CSSProperties;

        if (additionalButton.width && additionalButton.height) {
          highlights.push(additionalButton);
        }

        if (buttonCompass.width && buttonCompass.height) {
          highlights.push(buttonCompass);
        }
      }

      const spotlightStyles = (spotlight as HTMLElement).style.cssText;

      highlights.forEach((rect) => {
        if (!rect.width || !rect.height) return;
        const spotlightCopy = spotlight.cloneNode(true) as HTMLElement;
        spotlightCopy.style.cssText = spotlightStyles;
        const cleanedStyles = spotlightStyles.replace(
          /(?:top|left|right|bottom|transform):[^;]+;/g,
          "",
        );

        spotlightCopy.style.cssText = `
          ${cleanedStyles}
          top: ${rect.top}px;
          left: ${rect.left}px;
          width: ${rect.width}px;
          height: ${rect.height}px;
        `;

        overlay.appendChild(spotlightCopy);
      });
    };

    spotlightTimeoutRef.current = setTimeout(applySpotlight, 50);
  };

  const removeExtraSpotlightStyles = (forceUnlock = false) => {
    if (spotlightTimeoutRef.current) {
      clearTimeout(spotlightTimeoutRef.current);
      spotlightTimeoutRef.current = null;
    }

    if (forceUnlock && isScrollLocked) {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
      setIsScrollLocked(false);
    }

    const overlay = document.querySelector(".react-joyride__overlay");
    if (!overlay) return;

    const spotlights = overlay.querySelectorAll(".react-joyride__spotlight");
    if (spotlights.length <= 1) return;
    for (let i = 1; i < spotlights.length; i++) {
      spotlights[i].remove();
    }
  };

  const navigateToStep = (desiredStep: number, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    setState({ run: false, steps });

    setTimeout(() => {
      setTutorialStep(desiredStep);

      setState({
        run: true,
        steps: defineJoyrideSteps(tutorialContent),
      });

      setIsScrollLocked(true);
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";

      if (desiredStep === 1) {
        spotlightTimeoutRef.current = setTimeout(() => {
          addExtraSpotlightStyle();
        }, 100);
      }
    }, 50);
  };

  const progressComponent = (index: number) => {
    let color0 = "bg-[#001f5a]";
    let color1 = "bg-[#001f5a]";
    let color2 = "bg-[#001f5a]";
    switch (index) {
      case 0:
        color0 = "bg-white";
        color1 = "bg-[#001f5a]";
        color2 = "bg-[#001f5a]";
        break;
      case 1:
        color0 = "bg-white";
        color1 = "bg-white";
        color2 = "bg-[#001f5a]";
        break;
      case 2:
        color0 = "bg-white";
        color1 = "bg-white";
        color2 = "bg-white";
        break;
    }

    return (
      <div className="flex items-center">
        <div
          className={`rounded-full ${color0} cursor-pointer`}
          onClick={(e) => navigateToStep(0, e)}
          data-testid="tutorial-dot-0"
        />
        <div
          className={`rounded-full ${color1} cursor-pointer`}
          onClick={(e) => navigateToStep(1, e)}
          data-testid="tutorial-dot-1"
        />
        <div
          className={`rounded-full ${color2} cursor-pointer`}
          onClick={(e) => navigateToStep(2, e)}
          data-testid="tutorial-dot-2"
        />
      </div>
    );
  };

  const defineJoyrideSteps = (
    tutorialContent: (index: number) => JSX.Element | undefined,
  ) => [
    {
      content: tutorialContent(0),
      placement: "left" as const,
      showSkipButton: true,
      locale: { skip: " " },
      disableBeacon: true,
      target: "#button-dashboard-card",
      styles: {
        spotlight: {
          borderRadius: "18px",
          transform:
            window.innerWidth <= 370
              ? "scaleX(0.970) scaleY(0.89) translateY(1px)"
              : window.innerWidth <= 630
                ? "scaleX(0.965) scaleY(0.89) translateY(1px)"
                : window.innerWidth <= 768
                  ? "scaleX(0.970) scaleY(0.89) translateY(1px)"
                  : "scaleX(0.955) scaleY(0.88) translateY(1px)",
        },
      },
      floaterProps: {
        disableAnimation: true,
      },
    },
    {
      content: tutorialContent(1),
      placement:
        window.innerWidth <= 1426 ? ("top" as const) : ("right" as const),
      showSkipButton: true,
      locale: { skip: " " },
      disableBeacon: true,
      target: window.innerWidth > 800 ? "#button-bill" : "#button-piggy-bank",
      styles: {
        spotlight: {
          borderRadius: "18px",
          transform: "scaleX(0.915) scaleY(0.87) translateY(1px)",
        },
      },
      floaterProps: {
        disableAnimation: true,
      },
    },
    {
      content: tutorialContent(2),
      placement: "right" as const,
      showSkipButton: true,
      locale: { skip: " " },
      disableBeacon: true,
      target: "#searchBarRef",
      styles: {
        spotlight: {
          borderRadius: "9999px",
          transform: "scaleX(0.97) scaleY(0.77) translateY(1px)",
        },
      },
      floaterProps: {
        disableAnimation: true,
      },
    },
  ];

  const [{ run, steps }, setState] = useState<State>({
    run: !getTutorialState().shown,
    steps: defineJoyrideSteps(tutorialContent),
  });

  useEffect(() => {
    if (run) {
      setState((prevState) => ({
        ...prevState,
        steps: defineJoyrideSteps(tutorialContent),
      }));
    }
  }, [run, tutorialContent]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setWasSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [setWasSeen]);

  const handleJoyrideCallback = (data: any) => {
    const { action, index, status, type } = data;

    if (type === "step:before" && action !== "close") {
      setIsScrollLocked(true);
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
    }

    if (action === "close" || status === "finished") {
      setState({ run: false, steps });
      setTutorialState(true);
      removeExtraSpotlightStyles(true);

      setIsScrollLocked(false);
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
    } else if (action === "prev" && type === "step:after") {
      setTutorialStep(index - 1);
    } else if (action === "next" && type === "step:after") {
      if (index === steps.length - 1) {
        setState({ run: false, steps });
        removeExtraSpotlightStyles(true);
        setIsScrollLocked(false);
        document.body.style.overflow = "auto";
        document.body.style.height = "auto";
        setTutorialState(true);
        sendFinishOnboardingClick();
      } else {
        setTutorialStep(index + 1);
      }
    }
  };

  useEffect(() => {
    if (run) {
      setIsScrollLocked(true);
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
      isActiveRef.current = true;

      if (tutorialStep === 1) {
        addExtraSpotlightStyle();
      } else {
        removeExtraSpotlightStyles();
      }

      setTimeout(() => {
        const tooltip = document.querySelector(".react-joyride__tooltip");
        const actionsContainer = tooltip?.querySelector("div:last-child");

        if (!tooltip || !actionsContainer) return;

        const allDots = document.querySelectorAll(".tutorial-dots-container");
        allDots.forEach((dot) => dot.remove());

        const dotsContainer = document.createElement("div");
        dotsContainer.className = "tutorial-dots-container";

        const dotsRoot = document.createElement("div");
        dotsContainer.appendChild(dotsRoot);

        const element = progressComponent(tutorialStep);

        if (actionsContainer.firstChild) {
          actionsContainer.insertBefore(
            dotsContainer,
            actionsContainer.firstChild,
          );
        } else {
          actionsContainer.appendChild(dotsContainer);
        }

        if (typeof window !== "undefined" && element) {
          const root = createRoot(dotsRoot);
          root.render(element);

          const existingRootAttr = "data-dot-root-id";
          const rootId = `dot-root-${Date.now()}`;
          dotsRoot.setAttribute(existingRootAttr, rootId);
          (window as any).__dotRoots = (window as any).__dotRoots || {};
          (window as any).__dotRoots[rootId] = root;
        }
      }, 100);
    } else {
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";
      setIsScrollLocked(false);
      isActiveRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialStep, run]);

  useEffect(() => {
    const handleViewportChange = () => {
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }

      scrollDebounceRef.current = setTimeout(() => {
        if (isActiveRef.current && run) {
          if (tutorialStep === 1) {
            addExtraSpotlightStyle();

            if (window.innerWidth <= 800 || window.innerWidth > 800) {
              const updatedSteps = [...steps];
              if (updatedSteps[1]) {
                updatedSteps[1].target =
                  window.innerWidth > 800
                    ? "#button-bill"
                    : "#button-piggy-bank";
                updatedSteps[1].placement =
                  window.innerWidth <= 1426
                    ? ("top" as const)
                    : ("right" as const);
              }

              setState((prevState) => ({
                ...prevState,
                steps: updatedSteps,
              }));
            }
          }
        }
      }, 200);
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, { passive: true });

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange);
      document.body.style.overflow = "auto";
      document.body.style.height = "auto";

      if (spotlightTimeoutRef.current) {
        clearTimeout(spotlightTimeoutRef.current);
      }

      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialStep, run]);
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined") {
        if ((window as any).__dotRoots) {
          Object.values((window as any).__dotRoots).forEach((root: any) => {
            try {
              root.unmount();
            } catch (e) {
              console.error("Error unmounting root:", e);
            }
          });
          (window as any).__dotRoots = {};
        }

        const allDots = document.querySelectorAll(".tutorial-dots-container");
        allDots.forEach((dot) => dot.remove());
      }
    };
  }, []);

  useEffect(() => {
    if (run) {
      const allDots = document.querySelectorAll(".tutorial-dots-container");
      allDots.forEach((dot) => dot.remove());
    }
  }, [tutorialStep, run]);

  return (
    <div
      className={`${
        hasSearched || isLoading
          ? "bg-white"
          : "bg-gradient-to-b from-white to-[#BAD9FF]"
      } flex font-flexo flex-col min-h-screen justify-between w-full ${
        isScrollLocked ? "overflow-hidden" : ""
      }`}
    >
      <header className="mt-4 mx-auto pl-6 w-full max-w-[1200px]"></header>
      {run && (
        <button
          className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 bg-white rounded-full shadow-md z-[1001]"
          onClick={() => {
            setState({ run: false, steps });
            setIsScrollLocked(false);
            document.body.style.overflow = "auto";
            document.body.style.height = "auto";
            removeExtraSpotlightStyles(true);
            setTutorialState(true);
            sendCloseOnboardingClick(`Paso ${tutorialStep + 1}`);
          }}
          aria-label="Close"
          style={{
            color: "gray",
            fontSize: "18px",
            fontWeight: "bold",
            textShadow: "0 0 3px #FFFFFF",
          }}
        >
          ✕
        </button>
      )}
      <div ref={containerRef}>
        {hasSearched || isLoading ? <ResponsePage /> : <SearchPage />}
      </div>
      <Joyride
        callback={handleJoyrideCallback}
        run={run}
        steps={steps}
        showProgress
        showSkipButton
        stepIndex={tutorialStep}
        continuous={true}
        disableOverlayClose={true}
        spotlightClicks={false}
        disableScrolling={true}
        hideBackButton={false}
        scrollOffset={0}
        locale={{
          nextLabelWithProgress: "Siguiente",
          last: "Comenzar",
          back: "Atrás",
        }}
        styles={{
          options: {
            arrowColor: "#002A8D",
            backgroundColor: "#002A8D",
            overlayColor: "rgba(0, 0, 0, 0.60)",
            primaryColor: "#001f5a",
            textColor: "white",
            width: 340,
            zIndex: 1000,
          },
          buttonClose: {
            display: "none",
          },
          buttonSkip: {
            display: "none",
          },
          tooltip: {
            transition: "none",
          },
          tooltipContainer: {
            textAlign: "left",
          },
          overlay: {
            overflow: "hidden",
          },
        }}
        floaterProps={{
          disableAnimation: true,
          styles: {
            floater: {
              transition: "none",
              overflow: "hidden",
            },
          },
        }}
        debug={false}
      />
    </div>
  );
};

export default MainPage;
